/**
 * EXTENSUS — one mind (Jarvis + Builder together).
 */
import { fetchLaneBoard, postLaneCheckin } from './api';
import { INTENT_ROUTING_RULES } from './lib/intent.mjs';
import {
  PRESENCE_THROTTLE_KEY,
  LEGACY_PRESENCE_KEYS,
  latestPresenceStamp,
  shouldPulsePresence,
} from './components/backroomLoadPolicy.mjs';

export const EXTENSUS_ENTITY = {
  product: 'EXTENSUS',
  one_mind: true,
  technical_aliases: ['extensus', 'EXTENSUS', 'Extensus'],
  faces: {
    JARVIS: { lane: 'JARVIS', branch_prefix: 'jarvis/', role: 'cockpit' },
    BUILDER: { lane: 'BUILDER', branch_prefix: 'builder/', role: 'builder' },
  },
  board: '/api/agent-lanes',
  backroom_thread: 'coordination',
  team_cycle: ['IDEATE', 'REVIEW', 'AGREE', 'EXECUTE', 'HANDOFF', 'BLOCKER'],
};

export {
  classifyIntent,
  EXECUTE_RE,
  INTENT_ROUTING_RULES,
  proposeAction,
  CHAT_WRITE_TOOLS,
  CHAT_READ_TOOLS,
} from './lib/intent.mjs';

export function extensusSystemRules({ face = 'JARVIS', mode = 'converse' } = {}) {
  return [
    'You are EXTENSUS — one mind. Jarvis + Builder together.',
    'Product: EXTENSUS.',
    'A separate legal product must never mix with this cockpit identity.',
    'Legacy word Extensus may appear as technical alias only — not a second AI.',
    `Operational lane for this process: ${face}.`,
    'The operator is final authority.',
    'Standing order: keep building and changing. There is no permanent finished state. Advance and evolve.',
    'Engine: local or LAN Ollama only. Classifier for ticks, reasoner for hard work. Writes sandboxed. Daemon also wakes on file events.',
    INTENT_ROUTING_RULES,
    'CONVERSE / TALK: answer from context and read tools. No unlock lectures. No APPROVE theater.',
    'MUTATING: prepare the action payload and wait for operator APPROVE. Then execute for real. Do not print how-to manuals.',
    'Backroom first-class cycle: [IDEATE] share ideas · [REVIEW] improve others · [AGREE] owner= work= paths= · [EXECUTE] status · [HANDOFF] proof · [BLOCKER] stop.',
    'Roleplay is forbidden.',
    mode === 'execute'
      ? 'MODE=EXECUTE: prefer real tools; refuse fake completion.'
      : mode === 'ambiguous'
        ? 'MODE=AMBIGUOUS: ask Talk or Execute.'
        : 'MODE=CONVERSE: answer clearly.',
  ].join('\n');
}

function storageGet(key) {
  try {
    if (typeof localStorage === 'undefined') return '';
    return localStorage.getItem(key) || '';
  } catch {
    return '';
  }
}

function storageSet(key, value) {
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}

/** One presence slot for cockpit boot + Backroom tab + execute. */
export function presenceDue({ force = false } = {}) {
  return shouldPulsePresence(Date.now(), latestPresenceStamp(storageGet), undefined, force);
}

export function markPresencePulse() {
  const now = String(Date.now());
  storageSet(PRESENCE_THROTTLE_KEY, now);
  for (const key of LEGACY_PRESENCE_KEYS) storageSet(key, now);
}

export async function bootExtensusEntity({ source = 'pages-cockpit', forceCheckin = false } = {}) {
  const boardBefore = await fetchLaneBoard();
  const boardReadable = Boolean(
    boardBefore?.success ||
      boardBefore?.ok ||
      boardBefore?.presence ||
      Array.isArray(boardBefore?.active_claims) ||
      Array.isArray(boardBefore?.presence),
  );

  // Board read alone proves wire when board is live. One shared 15m slot
  // across boot + Backroom tab + execute — two localStorage keys used to double-fire.
  let checkin = { success: true, throttled: true, skipped: true };
  const throttled = !presenceDue({ force: forceCheckin });
  if (!throttled) {
    checkin = await postLaneCheckin({
      agent: 'EXTENSUS',
      lane: 'JARVIS',
      status: 'online',
      working_on: 'cockpit',
      branch: 'main',
      note: `entity-boot ${source}`,
    });
    if (checkin?.success || checkin?.ok || checkin?.presence) markPresencePulse();
  }

  const checkinOk = Boolean(
    throttled ||
      checkin?.success ||
      checkin?.ok ||
      checkin?.presence ||
      checkin?.status === 200 ||
      boardReadable,
  );
  const wired = boardReadable || checkinOk;

  return {
    entity: EXTENSUS_ENTITY.product,
    one_mind: true,
    repo: EXTENSUS_ENTITY.repo,
    jarvis: { ok: checkinOk, response: checkin, throttled },
    builder: { ok: checkinOk, response: checkin, throttled },
    board_before_ok: boardReadable,
    board_after: boardBefore,
    wired,
    checkin_throttled: throttled,
    roleplay: false,
    error: wired ? null : (checkin?.error || boardBefore?.error || 'board_unreachable'),
  };
}
